import React from 'react';
import {
  ThemeProvider as WebThemeProvider,
  createTheme,
} from '@toqio/toqio-web-components';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import type { ThemeTokens } from '../bridge/protocol';
import lumColors from './lum-colors.json';

/**
 * Builds the design-system theme from a flat colors map.
 *
 * **This is a near-verbatim port of `lum-web/src/ThemeProvider.js`** so the
 * forms-host renders with byte-for-byte the same theme structure that
 * lum-web feeds to `@toqio/toqio-web-components`. Keep both files in sync:
 * if lum-web adds a new token path here, mirror it.
 *
 * Why mirror instead of reinventing? The design-system components consume
 * many nested theme paths (`color.forms.default.focus.border`,
 * `color.feedback_notification.error.icon`, …). Mapping only a subset
 * lets unset paths fall back to the DS's built-in defaults (which is what
 * was happening before — the focus border was rendering with the DS
 * default blue instead of LUM's `#83A1B4`).
 *
 * Source of truth: `lum-web/src/ThemeProvider.js` `getTheme()`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildThemeFromColors = (colors: any): any => {
  // The theme is a large nested structure (a verbatim port of lum-web's
  // getTheme). Assemble it untyped before handing it to `createTheme`:
  // passing the object literal inline triggers an excess-property check
  // against styled-components' `DefaultTheme`, which we only augment with
  // `colors`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const theme: any = {
    typography: {
      fontFamily: colors.font_family,
      fontWeight: colors.font_base_weight,
    },
    placeholders_box_background_start: colors.placeholders_box_background_start,
    placeholders_box_background_end: colors.placeholders_box_background_end,
    placeholders_box_title_color: colors.placeholders_box_title_color,
    placeholders_box_text_color: colors.placeholders_box_text_color,
    placeholders_box_action_color: colors.placeholders_box_action_color,
    /* OLD COLOR VARIABLES (kept for parity with lum-web) */
    green: colors.green,
    red: colors.red,
    dark_orange: colors.dark_orange,
    color: {
      general: {
        font: {
          primary: colors.general_font_primary,
          secondary: colors.general_font_secondary,
          tertiary: colors.general_font_tertiary,
          quaternary: colors.general_font_quaternary,
        },
        dividers: colors.general_dividers,
        background: colors.general_background,
        scroll_bar: colors.general_scroll_bar,
      },
      containers: {
        shadowed: { background: colors.containers_shadowed_background },
        bordered: {
          border: colors.containers_bordered_border,
          background: colors.containers_bordered_background,
        },
      },
      modal_dialog: {
        background: colors.modal_dialog_background,
        overlay_background: colors.modal_dialog_overlay_background,
      },
      progress_and_loading: {
        main: colors.progress_and_loading_main,
        background: colors.progress_and_loading_background,
      },
      avatar: {
        icon: {
          icon: colors.avatar_icon_icon,
          background: colors.avatar_icon_background,
        },
        initials: {
          font: colors.avatar_initials_font,
          background: colors.avatar_initials_background,
        },
      },
      tag: {
        dark: { font: colors.tag_dark_font, background: colors.tag_dark_background },
        light: { font: colors.tag_light_font, background: colors.tag_light_background },
      },
      group_separator: {
        font: colors.group_separator_font,
        background: colors.group_separator_background,
      },
      table_row: { positive_background: colors.table_row_positive_background },
      standard_button: {
        primary: {
          default: {
            background: colors.standard_button_primary_default_background,
            font: colors.standard_button_primary_default_font,
            border: colors.standard_button_primary_default_border,
          },
          hover: {
            background: colors.standard_button_primary_hover_background,
            font: colors.standard_button_primary_hover_font,
            border: colors.standard_button_primary_hover_border,
          },
          focus: {
            background: colors.standard_button_primary_focus_background,
            font: colors.standard_button_primary_focus_font,
            border: colors.standard_button_primary_focus_border,
          },
          active: {
            background: colors.standard_button_primary_active_background,
            font: colors.standard_button_primary_active_font,
            border: colors.standard_button_primary_active_border,
          },
        },
        secondary: {
          default: {
            background: colors.standard_button_secondary_default_background,
            font: colors.standard_button_secondary_default_font,
            border: colors.standard_button_secondary_default_border,
          },
          hover: {
            background: colors.standard_button_secondary_hover_background,
            font: colors.standard_button_secondary_hover_font,
            border: colors.standard_button_secondary_hover_border,
          },
          focus: {
            background: colors.standard_button_secondary_focus_background,
            font: colors.standard_button_secondary_focus_font,
            border: colors.standard_button_secondary_focus_border,
          },
          active: {
            background: colors.standard_button_secondary_active_background,
            font: colors.standard_button_secondary_active_font,
            border: colors.standard_button_secondary_active_border,
          },
        },
        accent: {
          default: {
            background: colors.standard_button_accent_default_background,
            font: colors.standard_button_accent_default_font,
            border: colors.standard_button_accent_default_border,
          },
          hover: {
            background: colors.standard_button_accent_hover_background,
            font: colors.standard_button_accent_hover_font,
            border: colors.standard_button_accent_hover_border,
          },
          focus: {
            background: colors.standard_button_accent_focus_background,
            font: colors.standard_button_accent_focus_font,
            border: colors.standard_button_accent_focus_border,
          },
          active: {
            background: colors.standard_button_accent_active_background,
            font: colors.standard_button_accent_active_font,
            border: colors.standard_button_accent_active_border,
          },
        },
        critical: {
          default: {
            background: colors.standard_button_critical_default_background,
            font: colors.standard_button_critical_default_font,
            border: colors.standard_button_critical_default_border,
          },
          hover: {
            background: colors.standard_button_critical_hover_background,
            font: colors.standard_button_critical_hover_font,
            border: colors.standard_button_critical_hover_border,
          },
          focus: {
            background: colors.standard_button_critical_focus_background,
            font: colors.standard_button_critical_focus_font,
            border: colors.standard_button_critical_focus_border,
          },
          active: {
            background: colors.standard_button_critical_active_background,
            font: colors.standard_button_critical_active_font,
            border: colors.standard_button_critical_active_border,
          },
        },
      },
      icon_button: {
        primary: {
          default: {
            background: colors.icon_button_primary_default_background,
            icon: colors.icon_button_primary_default_icon,
            border: colors.icon_button_primary_default_border,
          },
          hover: {
            background: colors.icon_button_primary_hover_background,
            icon: colors.icon_button_primary_hover_icon,
            border: colors.icon_button_primary_hover_border,
          },
          focus: {
            background: colors.icon_button_primary_focus_background,
            icon: colors.icon_button_primary_focus_icon,
            border: colors.icon_button_primary_focus_border,
          },
          active: {
            background: colors.icon_button_primary_active_background,
            icon: colors.icon_button_primary_active_icon,
            border: colors.icon_button_primary_active_border,
          },
        },
        secondary: {
          default: {
            background: colors.icon_button_secondary_default_background,
            icon: colors.icon_button_secondary_default_icon,
            border: colors.icon_button_secondary_default_border,
          },
          hover: {
            background: colors.icon_button_secondary_hover_background,
            icon: colors.icon_button_secondary_hover_icon,
            border: colors.icon_button_secondary_hover_border,
          },
          focus: {
            background: colors.icon_button_secondary_focus_background,
            icon: colors.icon_button_secondary_focus_icon,
            border: colors.icon_button_secondary_focus_border,
          },
          active: {
            background: colors.icon_button_secondary_active_background,
            icon: colors.icon_button_secondary_active_icon,
            border: colors.icon_button_secondary_active_border,
          },
        },
        accent: {
          default: {
            background: colors.icon_button_accent_default_background,
            icon: colors.icon_button_accent_default_icon,
            border: colors.icon_button_accent_default_border,
          },
          hover: {
            background: colors.icon_button_accent_hover_background,
            icon: colors.icon_button_accent_hover_icon,
            border: colors.icon_button_accent_hover_border,
          },
          focus: {
            background: colors.icon_button_accent_focus_background,
            icon: colors.icon_button_accent_focus_icon,
            border: colors.icon_button_accent_focus_border,
          },
          active: {
            background: colors.icon_button_accent_active_background,
            icon: colors.icon_button_accent_active_icon,
            border: colors.icon_button_accent_active_border,
          },
        },
        critical: {
          default: {
            background: colors.icon_button_critical_default_background,
            icon: colors.icon_button_critical_default_icon,
            border: colors.icon_button_critical_default_border,
          },
          hover: {
            background: colors.icon_button_critical_hover_background,
            icon: colors.icon_button_critical_hover_icon,
            border: colors.icon_button_critical_hover_border,
          },
          focus: {
            background: colors.icon_button_critical_focus_background,
            icon: colors.icon_button_critical_focus_icon,
            border: colors.icon_button_critical_focus_border,
          },
          active: {
            background: colors.icon_button_critical_active_background,
            icon: colors.icon_button_critical_active_icon,
            border: colors.icon_button_critical_active_border,
          },
        },
      },
      chip: {
        status: {
          success: {
            font: colors.chip_status_success_font,
            background: colors.chip_status_success_background,
          },
          info: {
            font: colors.chip_status_info_font,
            background: colors.chip_status_info_background,
          },
          warning: {
            font: colors.chip_status_warning_font,
            background: colors.chip_status_warning_background,
          },
          danger: {
            font: colors.chip_status_danger_font,
            background: colors.chip_status_danger_background,
          },
          neutral: {
            font: colors.chip_status_neutral_font,
            background: colors.chip_status_neutral_background,
          },
        },
        miscellany: {
          grey_blue: {
            font: colors.chip_miscellany_grey_blue_font,
            background: colors.chip_miscellany_grey_blue_background,
          },
          purple: {
            font: colors.chip_miscellany_purple_font,
            background: colors.chip_miscellany_purple_background,
          },
          bermillion: {
            font: colors.chip_miscellany_bermillion_font,
            background: colors.chip_miscellany_bermillion_background,
          },
          sky: {
            font: colors.chip_miscellany_sky_font,
            background: colors.chip_miscellany_sky_background,
          },
          orange: {
            font: colors.chip_miscellany_orange_font,
            background: colors.chip_miscellany_orange_background,
          },
          grass: {
            font: colors.chip_miscellany_grass_font,
            background: colors.chip_miscellany_grass_background,
          },
          brownie: {
            font: colors.chip_miscellany_brownie_font,
            background: colors.chip_miscellany_brownie_background,
          },
        },
      },
      link: {
        default: colors.link_default,
        critical: colors.link_critical,
      },
      tooltip: {
        font: colors.tooltip_font,
        background: colors.tooltip_background,
      },
      contextual_modal_box: {
        background: colors.contextual_modal_box_background,
        default: { links: colors.contextual_modal_box_default_links },
        critical: { links: colors.contextual_modal_box_critical_links },
      },
      rapid_filter: {
        default: {
          font_primary: colors.rapid_filter_default_font_primary,
          font_secondary: colors.rapid_filter_default_font_secondary,
          border: colors.rapid_filter_default_border,
          background: colors.rapid_filter_default_background,
        },
        selected: {
          font_primary: colors.rapid_filter_selected_font_primary,
          font_secondary: colors.rapid_filter_selected_font_secondary,
          border: colors.rapid_filter_selected_border,
          background: colors.rapid_filter_selected_background,
        },
        disabled: {
          font_primary: colors.rapid_filter_disabled_font_primary,
          font_secondary: colors.rapid_filter_disabled_font_secondary,
          border: colors.rapid_filter_disabled_border,
          background: colors.rapid_filter_disabled_background,
        },
      },
      selection_box: {
        unselected: {
          font_primary: colors.selection_box_unselected_font_primary,
          font_secondary: colors.selection_box_unselected_font_secondary,
          border: colors.selection_box_unselected_border,
          background: colors.selection_box_unselected_background,
          select_all_icon: colors.selection_box_unselected_select_all_icon,
          select_all_background: colors.selection_box_unselected_select_all_background,
        },
        selected: {
          font_primary: colors.selection_box_selected_font_primary,
          font_secondary: colors.selection_box_selected_font_secondary,
          border: colors.selection_box_selected_border,
          background: colors.selection_box_selected_background,
          icon: colors.selection_box_selected_icon,
          select_all_icon: colors.selection_box_selected_select_all_icon,
          select_all_background: colors.selection_box_selected_select_all_background,
        },
      },
      feedback_notification: {
        info: {
          font: colors.feedback_notification_info_font,
          icon: colors.feedback_notification_info_icon,
          background: colors.feedback_notification_info_background,
          border: colors.feedback_notification_info_border,
        },
        warning: {
          font: colors.feedback_notification_warning_font,
          icon: colors.feedback_notification_warning_icon,
          background: colors.feedback_notification_warning_background,
          border: colors.feedback_notification_warning_border,
        },
        error: {
          font: colors.feedback_notification_alert_font,
          icon: colors.feedback_notification_alert_icon,
          background: colors.feedback_notification_alert_background,
          border: colors.feedback_notification_alert_border,
        },
        success: {
          font: colors.feedback_notification_success_font,
          icon: colors.feedback_notification_success_icon,
          background: colors.feedback_notification_success_background,
          border: colors.feedback_notification_success_border,
        },
      },
      sidebar: {
        font_legal: colors.sidebar_font_legal,
        font_active_section: colors.sidebar_font_active_section,
        font_non_active_section: colors.sidebar_font_non_active_section,
        active_section_badge: colors.sidebar_active_section_badge,
        divider: colors.sidebar_divider,
        background: colors.sidebar_background,
      },
      topbar: {
        background: colors.topbar_background,
        expandable_background: colors.topbar_expandable_background,
      },
      navigation_row: {
        icon: colors.navigation_row_icon,
        font: colors.navigation_row_font,
        arrow: colors.navigation_row_arrow,
        background: colors.navigation_row_background,
      },
      tabs: {
        selected_tab: {
          selector: colors.tabs_selected_tab_selector,
          font: colors.tabs_selected_tab_font,
          background: colors.tabs_selected_tab_background,
        },
        unselected_tab: {
          font: colors.tabs_unselected_tab_font,
          background: colors.tabs_unselected_tab_background,
        },
      },
      toast: {
        success: {
          font: colors.toast_success_font,
          background: colors.toast_success_background,
        },
        wrong: {
          font: colors.toast_wrong_font,
          background: colors.toast_wrong_background,
        },
      },
      bullet_list: { icon: colors.bullet_list_icon },
      forms: {
        default: {
          background: colors.forms_default_background,
          border: colors.forms_default_border,
          placeholder_font: colors.forms_default_placeholder_font,
          primary_font: colors.forms_default_primary_font,
          secondary_font: colors.forms_default_secondary_font,
          innericons_and_selected: colors.forms_default_innericons_and_selected,
          unselected: colors.forms_default_unselected,
          innericons_background: colors.forms_default_innericons_background,
          focus: {
            background: colors.forms_default_focus_background,
            border: colors.forms_default_focus_border,
            placeholder_font: colors.forms_default_focus_placeholder_font,
            primary_font: colors.forms_default_focus_primary_font,
            secondary_font: colors.forms_default_focus_secondary_font,
            innericons_and_selected: colors.forms_default_focus_innericons_and_selected,
            unselected: colors.forms_default_focus_unselected,
            innericons_background: colors.forms_default_focus_innericons_background,
          },
        },
        error: {
          background: colors.forms_error_background,
          border: colors.forms_error_border,
          placeholder_font: colors.forms_error_placeholder_font,
          primary_font: colors.forms_error_primary_font,
          secondary_font: colors.forms_error_secondary_font,
          innericons_and_selected: colors.forms_error_innericons_and_selected,
          unselected: colors.forms_error_unselected,
          innericons_background: colors.forms_error_innericons_background,
          focus: {
            background: colors.forms_error_focus_background,
            border: colors.forms_error_focus_border,
            placeholder_font: colors.forms_error_focus_placeholder_font,
            primary_font: colors.forms_error_focus_primary_font,
            secondary_font: colors.forms_error_focus_secondary_font,
            innericons_and_selected: colors.forms_error_focus_innericons_and_selected,
            unselected: colors.forms_error_focus_unselected,
            innericons_background: colors.forms_error_focus_innericons_background,
          },
        },
      },
      help: {
        info: { icon: colors.help_info_icon, font: colors.general_font_tertiary },
        warning: { icon: colors.help_warning_icon, font: colors.general_font_tertiary },
        error: { icon: colors.help_error_icon, font: colors.help_error_font },
        link: { icon: colors.help_info_icon, font: colors.link_default },
      },
      badge: { background: colors.badge_background },
      table: {
        header: {
          label: colors.table_header_label,
          background: colors.table_header_background,
          text_field: {
            font: colors.table_header_text_field_font,
            border: colors.table_header_text_field_border,
            background: colors.table_header_text_field_background,
          },
        },
      },
    },
    radii: {
      radius4: `border-radius: ${colors.radii_radius4}px;`,
      radius8: `border-radius: ${colors.radii_radius8}px;`,
      radius12: `border-radius: ${colors.radii_radius12}px;`,
      radius4Value: colors.radii_radius4,
      radius8Value: colors.radii_radius8,
      radius12Value: colors.radii_radius12,
    },
  };
  return createTheme(theme);
};

/**
 * Fallback theme used in standalone dev mode (no host attached). It is the
 * real LUM tenant palette — a verbatim copy of
 * `lum-web/src/styles/theme/lum/colors.json`. Keep them in sync.
 */
const FALLBACK_TOKENS: ThemeTokens = lumColors as ThemeTokens;

/**
 * Augment styled-components DefaultTheme so our custom styled-components
 * can read `theme.colors.*` flat tokens (in addition to the nested DS
 * paths that DS components consume).
 */
declare module 'styled-components' {
  export interface DefaultTheme {
    colors: ThemeTokens;
  }
}

type Props = {
  theme: ThemeTokens | null;
  children: React.ReactNode;
};

/**
 * Dual theme provider. Wraps children in:
 *
 *   1. `WebThemeProvider` from `@toqio/toqio-web-components` — feeds the
 *      nested DefaultTheme to every DS component (Button, TextField,
 *      Checkbox, …).
 *   2. `StyledThemeProvider` from `styled-components` — same theme object,
 *      so custom styled-components in FormShell / steps can read both the
 *      nested DS structure (`theme.color.forms.default.focus.border`) AND
 *      the flat tokens (`theme.colors.primary_color`).
 *
 * The theme is built via a near-verbatim port of
 * `lum-web/src/ThemeProvider.js`. Both apps therefore feed the DS
 * components an identical theme shape.
 */
export const ThemeProvider: React.FC<Props> = ({ theme, children }) => {
  const tokens = theme ?? FALLBACK_TOKENS;
  const built = { ...buildThemeFromColors(tokens), colors: tokens };

  return (
    <WebThemeProvider theme={built}>
      <StyledThemeProvider theme={built}>{children}</StyledThemeProvider>
    </WebThemeProvider>
  );
};
